/**
 * JS-side vertex provenance utilities — the mirror of Rust's `ProvenanceBuilder`
 * for ops that don't cross the WASM boundary (cleanUpMesh, separate,
 * joinObjects, mergeObjects, the JS modifier cores).
 *
 * The shape (`remap` + CSR `parentOffsets` / `parents`) matches the Rust
 * crate exactly so that the same `reconcilePerVertexAttributes` helper
 * processes WASM-produced and JS-produced provenances uniformly. There is
 * one mechanism, not two.
 */

import type { VertexProvenance } from "../wasm/editOps.ts";
import { VERTEX_REMOVED } from "../wasm/editOps.ts";
import { findEdgeIndex, withEdgeCreases } from "./topology.ts";
import type {
  FaceAttributeTable,
  FaceGroup,
  PolygonTopology,
  SceneObject,
  VertexGroup,
} from "./types.ts";
import { normalizeFaceAttributes } from "./types.ts";

/**
 * Identity provenance: every vert maps to itself with a single parent.
 * Use for ops that don't change vertex count or layout.
 */
export function identityProvenance(vertexCount: number): VertexProvenance {
  const remap = new Int32Array(vertexCount);
  const parentOffsets = new Uint32Array(vertexCount + 1);
  const parents = new Uint32Array(vertexCount);
  for (let i = 0; i < vertexCount; i++) {
    remap[i] = i;
    parentOffsets[i] = i;
    parents[i] = i;
  }
  parentOffsets[vertexCount] = vertexCount;
  return { remap, parentOffsets, parents };
}

/**
 * Build a provenance from a pre-computed compact remap table — the same
 * shape `topology.compactUnreferencedVertices()` and the modifier cores
 * already produce. Mirrors Rust's `ProvenanceBuilder::from_compact_remap`:
 * surviving olds with the same new index are bucketed together as parents
 * (the natural merge collapse), removed olds (`VERTEX_REMOVED`) are absent
 * from any parents bucket.
 */
export function provenanceFromCompactRemap(
  remap: Int32Array,
): VertexProvenance {
  const oldCount = remap.length;
  let newCount = 0;
  for (let i = 0; i < oldCount; i++) {
    const r = remap[i];
    if (r >= 0 && r + 1 > newCount) newCount = r + 1;
  }

  // Bucket parents per new index — start by counting occupants for CSR sizing.
  const counts = new Uint32Array(newCount);
  for (let i = 0; i < oldCount; i++) {
    const r = remap[i];
    if (r >= 0) counts[r]++;
  }
  const parentOffsets = new Uint32Array(newCount + 1);
  for (let i = 0; i < newCount; i++)
    parentOffsets[i + 1] = parentOffsets[i] + counts[i];

  const parents = new Uint32Array(parentOffsets[newCount]);
  const cursor = new Uint32Array(newCount);
  for (let oldIdx = 0; oldIdx < oldCount; oldIdx++) {
    const newIdx = remap[oldIdx];
    if (newIdx < 0) continue;
    parents[parentOffsets[newIdx] + cursor[newIdx]] = oldIdx;
    cursor[newIdx]++;
  }

  // The remap we received is already in the canonical form — copy so the
  // caller's buffer isn't aliased into the returned provenance.
  return { remap: new Int32Array(remap), parentOffsets, parents };
}

/**
 * Compose two provenances `a → b` and `b → c` into `a → c`. Used by
 * orchestration code that runs two compaction passes back-to-back
 * (cleanUpMesh's own multi-pass cleanup, separate's extract-then-compact,
 * mergeObjects' join-then-weld). Mirrors Rust's `ProvenanceBuilder::compose`
 * exactly so behaviour is identical on both sides of the boundary.
 */
export function composeProvenance(
  first: VertexProvenance,
  second: VertexProvenance,
): VertexProvenance {
  const aOldCount = first.remap.length;
  const bOldCount = second.remap.length;

  const remap = new Int32Array(aOldCount).fill(VERTEX_REMOVED);
  // Walk every old_a, project through first → mid_b, then second → new_c,
  // collecting the original old_a's that land at each new_c.
  const parentBuckets: Map<number, number[]> = new Map();
  let maxNew = -1;
  for (let oldA = 0; oldA < aOldCount; oldA++) {
    const mid = first.remap[oldA];
    if (mid < 0 || mid >= bOldCount) continue;
    const newC = second.remap[mid];
    if (newC < 0) continue;
    remap[oldA] = newC;
    let bucket = parentBuckets.get(newC);
    if (!bucket) {
      bucket = [];
      parentBuckets.set(newC, bucket);
    }
    bucket.push(oldA);
    if (newC > maxNew) maxNew = newC;
  }

  // Stage-2 novel verts (new_c indices not reached by the forward walk above)
  // need their parents translated from b-space back to a-space via first's
  // parents table.
  const secondNewCount =
    second.parentOffsets.length === 0 ? 0 : second.parentOffsets.length - 1;
  for (let newC = 0; newC < secondNewCount; newC++) {
    if (parentBuckets.has(newC)) continue;
    const bStart = second.parentOffsets[newC];
    const bEnd = second.parentOffsets[newC + 1];
    if (bEnd === bStart) {
      // Truly novel (no parents in b-space) — keep as orphan in a-space too.
      parentBuckets.set(newC, []);
      if (newC > maxNew) maxNew = newC;
      continue;
    }
    const aParents: number[] = [];
    for (let j = bStart; j < bEnd; j++) {
      const bParent = second.parents[j];
      const aStart = first.parentOffsets[bParent];
      const aEnd = first.parentOffsets[bParent + 1];
      for (let k = aStart; k < aEnd; k++) {
        const aParent = first.parents[k];
        if (!aParents.includes(aParent)) aParents.push(aParent);
      }
    }
    parentBuckets.set(newC, aParents);
    if (newC > maxNew) maxNew = newC;
  }

  const newCount = Math.max(0, maxNew + 1);
  const parentOffsets = new Uint32Array(newCount + 1);
  let total = 0;
  for (let i = 0; i < newCount; i++) {
    const bucket = parentBuckets.get(i);
    parentOffsets[i] = total;
    total += bucket ? bucket.length : 0;
  }
  parentOffsets[newCount] = total;
  const parents = new Uint32Array(total);
  for (let i = 0; i < newCount; i++) {
    const bucket = parentBuckets.get(i);
    if (!bucket) continue;
    let cursor = parentOffsets[i];
    for (const p of bucket) {
      parents[cursor++] = p;
    }
  }

  return { remap, parentOffsets, parents };
}

/**
 * Build a provenance from a flat "source old vert per new vert" table.
 *
 * `srcForNew[newIdx] = oldIdx | -1` (or any negative value for a novel
 * orphan new vert with no parent). The common case for modifier cores that
 * produce a 1:1-parent output (mirror, array, solidify, skin tube verts,
 * instance copies): every new vert has exactly one source vert.
 *
 * `oldVertCount` is the input's vert count (domain of the `remap` half);
 * `srcForNew.length` is the output's vert count.
 */
export function provenanceFromSourceMap(
  oldVertCount: number,
  srcForNew: Int32Array,
): VertexProvenance {
  const newCount = srcForNew.length;
  const remap = new Int32Array(oldVertCount).fill(VERTEX_REMOVED);
  // For each new vert with a source, mark that source's remap entry. If
  // multiple new verts share a source (e.g. array copies), the last wins —
  // `remap[oldIdx]` only names ONE new index, by convention the first copy.
  // Array/mirror callers iterate in order, so the earliest/canonical copy
  // ends up in `remap` naturally.
  for (let newIdx = newCount - 1; newIdx >= 0; newIdx--) {
    const src = srcForNew[newIdx];
    if (src >= 0 && src < oldVertCount) {
      remap[src] = newIdx;
    }
  }
  const parentOffsets = new Uint32Array(newCount + 1);
  let cursor = 0;
  for (let i = 0; i < newCount; i++) {
    parentOffsets[i] = cursor;
    if (srcForNew[i] >= 0) cursor++;
  }
  parentOffsets[newCount] = cursor;
  const parents = new Uint32Array(cursor);
  let p = 0;
  for (let i = 0; i < newCount; i++) {
    const src = srcForNew[i];
    if (src >= 0) parents[p++] = src >>> 0;
  }
  return { remap, parentOffsets, parents };
}

/**
 * Append a "novel" new vertex to an existing provenance, returning a new
 * provenance one vertex larger. Builder-style helper for ops that
 * incrementally produce new verts (separate, modifier cores) — kept
 * functional / immutable so it composes naturally with the rest of the
 * provenance API.
 */
export function appendNewVert(
  prov: VertexProvenance,
  parents: readonly number[],
): VertexProvenance {
  const oldNewCount =
    prov.parentOffsets.length === 0 ? 0 : prov.parentOffsets.length - 1;
  const newNewCount = oldNewCount + 1;
  const nextOffsets = new Uint32Array(newNewCount + 1);
  nextOffsets.set(prov.parentOffsets);
  nextOffsets[newNewCount] = prov.parents.length + parents.length;
  const nextParents = new Uint32Array(prov.parents.length + parents.length);
  nextParents.set(prov.parents);
  for (let i = 0; i < parents.length; i++) {
    nextParents[prov.parents.length + i] = parents[i];
  }
  return {
    remap: prov.remap,
    parentOffsets: nextOffsets,
    parents: nextParents,
  };
}

// ---------------------------------------------------------------------------
// Per-attribute reconcilers — used by `applyResult`, `separate`, modifier
// apply, and any other code path that needs to project a per-vertex array
// through a provenance.
// ---------------------------------------------------------------------------

/**
 * Project per-group index sets forward through a provenance. A new vert
 * inherits group membership iff ANY of its OLD parents was in the group —
 * the natural "union of parents" semantic for an index-set attribute. Empty
 * groups post-remap are kept (matching Blender — empty groups stay in the
 * panel until the user explicitly deletes them).
 */
export function remapVertexGroups(
  groups: VertexGroup[],
  prov: VertexProvenance,
): VertexGroup[] | undefined {
  const newCount =
    prov.parentOffsets.length === 0 ? 0 : prov.parentOffsets.length - 1;
  const next: VertexGroup[] = groups.map((g) => {
    const indices = new Set<number>();
    for (let i = 0; i < newCount; i++) {
      const start = prov.parentOffsets[i];
      const end = prov.parentOffsets[i + 1];
      for (let j = start; j < end; j++) {
        if (g.indices.has(prov.parents[j])) {
          indices.add(i);
          break;
        }
      }
    }
    return { ...g, indices };
  });
  return next.length > 0 ? next : undefined;
}

/**
 * Project a per-vertex scalar Float32Array forward through a provenance.
 * New verts take the average of their parents' values, or `default_` when
 * they have no parents.
 */
export function remapPerVertexFloat(
  oldArr: Float32Array,
  prov: VertexProvenance,
  newVertCount: number,
  default_: number,
): Float32Array {
  const out = new Float32Array(newVertCount);
  for (let i = 0; i < newVertCount; i++) {
    const start = prov.parentOffsets[i];
    const end = prov.parentOffsets[i + 1];
    if (end === start) {
      out[i] = default_;
      continue;
    }
    let sum = 0;
    for (let j = start; j < end; j++) sum += oldArr[prov.parents[j]];
    out[i] = sum / (end - start);
  }
  return out;
}

/**
 * Project a per-vertex stride-3 Float32Array (positions, displacements)
 * forward through a provenance. Same averaging rule as the scalar version,
 * but per component.
 *
 * `defaults` may be either a 3-element vec3 (use that constant for every
 * orphan new vert) or a Float32Array the same length as the new vert array
 * (use the corresponding stride-3 entry as the default — used by
 * `baseVertices`, where a no-parent new vert should default to the mesh's
 * actual new position).
 */
export function remapPerVertexFloat3(
  oldArr: Float32Array,
  prov: VertexProvenance,
  newVertCount: number,
  defaults: Float32Array,
): Float32Array {
  const usePerVertexDefault = defaults.length === newVertCount * 3;
  const out = new Float32Array(newVertCount * 3);
  for (let i = 0; i < newVertCount; i++) {
    const start = prov.parentOffsets[i];
    const end = prov.parentOffsets[i + 1];
    if (end === start) {
      if (usePerVertexDefault) {
        out[i * 3] = defaults[i * 3];
        out[i * 3 + 1] = defaults[i * 3 + 1];
        out[i * 3 + 2] = defaults[i * 3 + 2];
      } else {
        out[i * 3] = defaults[0];
        out[i * 3 + 1] = defaults[1];
        out[i * 3 + 2] = defaults[2];
      }
      continue;
    }
    let sx = 0;
    let sy = 0;
    let sz = 0;
    for (let j = start; j < end; j++) {
      const p = prov.parents[j];
      sx += oldArr[p * 3];
      sy += oldArr[p * 3 + 1];
      sz += oldArr[p * 3 + 2];
    }
    const inv = 1 / (end - start);
    out[i * 3] = sx * inv;
    out[i * 3 + 1] = sy * inv;
    out[i * 3 + 2] = sz * inv;
  }
  return out;
}

/** Default vec3 = (0, 0, 0), shared by callers that need a zero displacement. */
export const ZERO3: Float32Array = new Float32Array([0, 0, 0]);

/**
 * Project per-edge crease weights forward through a topology change using a
 * `VertexProvenance`. For each new edge `(u, v)`, enumerate every
 * `(pu, pv) ∈ parents(u) × parents(v)`; each pair that corresponds to an old
 * edge contributes that edge's crease to an average.
 *
 * This side-steps a dedicated per-op edge-provenance system: VertexProvenance
 * already pins down which old vertices a new vertex came from, and an edge
 * between two new vertices descends from an old edge iff that pair of
 * parents was itself an edge. Works uniformly across subdivide (new midpoints
 * inherit from the split parent edge), extrude (ring edges inherit, side
 * connector edges default to 0), merge (creases on coalescing edges average),
 * and dissolve (dropped edges simply never match a new one).
 *
 * Returns `undefined` when the input has no creases, or when every projected
 * weight is 0 — preserving the "omit-when-empty" invariant on
 * `PolygonTopology.edgeCreases`.
 */
export function remapEdgeCreases(
  oldTopology: PolygonTopology,
  oldCreases: Float32Array | undefined,
  newTopology: PolygonTopology,
  provenance: VertexProvenance,
): Float32Array | undefined {
  if (!oldCreases || oldCreases.length === 0) return undefined;
  const newEdgeCount = newTopology.edges.length / 2;
  if (newEdgeCount === 0) return undefined;

  const out = new Float32Array(newEdgeCount);
  let anyNonZero = false;

  const { parentOffsets, parents } = provenance;
  const newEdges = newTopology.edges;

  for (let ei = 0; ei < newEdgeCount; ei++) {
    const nv0 = newEdges[ei * 2];
    const nv1 = newEdges[ei * 2 + 1];

    const s0 = parentOffsets[nv0];
    const e0 = parentOffsets[nv0 + 1];
    const s1 = parentOffsets[nv1];
    const e1 = parentOffsets[nv1 + 1];
    if (s0 === e0 || s1 === e1) continue; // orphan endpoint → no ancestor edge

    let sum = 0;
    let count = 0;
    for (let i = s0; i < e0; i++) {
      const pa = parents[i];
      for (let j = s1; j < e1; j++) {
        const pb = parents[j];
        if (pa === pb) continue;
        const oldEi = findEdgeIndex(oldTopology, pa, pb);
        if (oldEi >= 0 && oldEi < oldCreases.length) {
          sum += oldCreases[oldEi];
          count++;
        }
      }
    }

    if (count > 0) {
      const w = sum / count;
      out[ei] = w;
      if (w !== 0) anyNonZero = true;
    }
  }

  return anyNonZero ? out : undefined;
}

/**
 * Reconcile topology-level attributes from a source topology onto a target
 * topology through provenance, without overwriting attributes the target
 * already carries explicitly.
 *
 * Today this covers `edgeCreases`. Callers that construct a target topology
 * with creases already attached keep that authoritative result; callers that
 * only rebuilt topology structure can route through this helper to fill the
 * crease field from provenance in one place.
 */
export function inheritTopologyAttributes(
  sourceTopology: PolygonTopology,
  targetTopology: PolygonTopology,
  provenance: VertexProvenance,
): PolygonTopology {
  if (targetTopology.edgeCreases) return targetTopology;
  if (!sourceTopology.edgeCreases) return targetTopology;
  return withEdgeCreases(
    targetTopology,
    remapEdgeCreases(
      sourceTopology,
      sourceTopology.edgeCreases,
      targetTopology,
      provenance,
    ),
  );
}

/**
 * Project all per-vertex attributes from a source SceneObject onto a freshly
 * built target with new geometry, via the provenance that maps source's
 * vertex space to target's vertex space.
 *
 * Used by every code path that constructs a new SceneObject from an existing
 * one with different topology: separate (extracted + remaining), joinObjects,
 * mergeObjects, applyModifier (Mirror, Subsurf, etc.). Single helper, single
 * policy, one place to update if a new per-vertex attribute is added to
 * `SceneObject`.
 */
export function inheritPerVertexAttributes(
  source: SceneObject,
  target: SceneObject,
  provenance: VertexProvenance,
): SceneObject {
  const newVc = target.vertices.length / 3;
  let out = target;
  if (source.vertexGroups) {
    out = {
      ...out,
      vertexGroups: remapVertexGroups(source.vertexGroups, provenance),
    };
  }
  if (source.skinRadii) {
    out = {
      ...out,
      skinRadii: remapPerVertexFloat(source.skinRadii, provenance, newVc, 1),
    };
  }
  if (source.baseVertices) {
    out = {
      ...out,
      // Orphan new verts default to their own current position so the sculpt
      // base stays anchored to the bake.
      baseVertices: remapPerVertexFloat3(
        source.baseVertices,
        provenance,
        newVc,
        target.vertices,
      ),
    };
  }
  return out;
}

/**
 * Build a provenance for a "concat plus identity" join: the first
 * `targetVertCount` verts in the result space are the target's verts at
 * identity indices; the remaining `appendedVertCount` verts are novel
 * (no parents). Used by `joinObjects` and as the stage-1 input to
 * `mergeObjects` (which then composes a weld provenance on top).
 */
export function provenanceFromJoinAppend(
  targetVertCount: number,
  appendedVertCount: number,
): VertexProvenance {
  const totalNew = targetVertCount + appendedVertCount;
  const remap = new Int32Array(targetVertCount);
  const parentOffsets = new Uint32Array(totalNew + 1);
  // Target verts at identity: each has one parent (itself).
  for (let i = 0; i < targetVertCount; i++) {
    remap[i] = i;
    parentOffsets[i] = i;
  }
  parentOffsets[targetVertCount] = targetVertCount;
  // Appended novel verts: zero parents each. Offsets repeat to indicate
  // empty parent buckets.
  for (let i = targetVertCount; i < totalNew; i++) {
    parentOffsets[i + 1] = parentOffsets[i];
  }
  const parents = new Uint32Array(targetVertCount);
  for (let i = 0; i < targetVertCount; i++) parents[i] = i;
  return { remap, parentOffsets, parents };
}

// ===========================================================================
// Face provenance — per-face mirror of `VertexProvenance`
// ===========================================================================

/** Sentinel in `FaceProvenance.faceRemap`: this old face has no surviving
 *  successor (deleted or fully dissolved). Mirrors `VERTEX_REMOVED`. */
export const FACE_REMOVED = -1;

/**
 * "Where did each face come from" record produced for ops that need to
 * forward-project per-face data (material slot, shading override, face-group
 * memberships).
 *
 * - `faceRemap[oldIdx]` = the new face index this old face occupies, or
 *   `FACE_REMOVED` if it was deleted / dissolved away. Iterate in the OLD
 *   direction to forward-project per-face *index* sets (face groups).
 *
 * - `faceParents` (CSR via `faceParentOffsets`): for each NEW face `i`, the
 *   OLD face indices it inherits from are
 *   `faceParents[faceParentOffsets[i] .. faceParentOffsets[i+1]]`. A
 *   surviving face has one parent (itself); a face produced by face-merge
 *   ops (dissolve faces, weld) has many; a freshly-created face (extrude
 *   cap, inset, subdivide) has zero. Iterate in the NEW direction to
 *   back-project per-face *value* arrays — first parent for slot/shading
 *   (single-valued), union for face-group membership.
 *
 * Both halves are derivable from each other; both are provided for the
 * same reason as `VertexProvenance`: each consumer picks the natural
 * iteration direction for its data shape.
 */
export interface FaceProvenance {
  /** length = old face count; `faceRemap[old] = new index | FACE_REMOVED` */
  faceRemap: Int32Array;
  /** length = new face count + 1; CSR offsets into `faceParents` */
  faceParentOffsets: Uint32Array;
  /** flat CSR of OLD-face parent indices for each NEW face */
  faceParents: Uint32Array;
}

/**
 * Identity face provenance: every face maps to itself with a single parent.
 * Use for ops that don't change face count or face order (vertex-only edits,
 * pure transforms).
 */
export function identityFaceProvenance(faceCount: number): FaceProvenance {
  const faceRemap = new Int32Array(faceCount);
  const faceParentOffsets = new Uint32Array(faceCount + 1);
  const faceParents = new Uint32Array(faceCount);
  for (let i = 0; i < faceCount; i++) {
    faceRemap[i] = i;
    faceParentOffsets[i] = i;
    faceParents[i] = i;
  }
  faceParentOffsets[faceCount] = faceCount;
  return { faceRemap, faceParentOffsets, faceParents };
}

/**
 * Empty face provenance: every old face removed, no new faces. Used by ops
 * that strip all faces (e.g. delete-everything). The reconciler sees an
 * empty parent table and drops all per-face attributes.
 */
export function emptyFaceProvenance(oldFaceCount: number): FaceProvenance {
  const faceRemap = new Int32Array(oldFaceCount);
  faceRemap.fill(FACE_REMOVED);
  return {
    faceRemap,
    faceParentOffsets: new Uint32Array(1),
    faceParents: new Uint32Array(0),
  };
}

/**
 * Build face provenance from a flat "source old face per new face" table.
 *
 * `srcForNewFace[newIdx] = oldFaceIdx | -1`. New faces with no source parent
 * inherit per-face defaults. This is the direct face-domain equivalent of
 * `provenanceFromSourceMap` and is the preferred contract for modifiers whose
 * generated face ordering is known exactly (subsurf, solidify, array, mirror).
 */
export function faceProvenanceFromSourceMap(
  oldFaceCount: number,
  srcForNewFace: Int32Array,
): FaceProvenance {
  const newFaceCount = srcForNewFace.length;
  const faceRemap = new Int32Array(oldFaceCount).fill(FACE_REMOVED);
  for (let newIdx = newFaceCount - 1; newIdx >= 0; newIdx--) {
    const src = srcForNewFace[newIdx];
    if (src >= 0 && src < oldFaceCount) {
      faceRemap[src] = newIdx;
    }
  }

  const faceParentOffsets = new Uint32Array(newFaceCount + 1);
  let parentCount = 0;
  for (let i = 0; i < newFaceCount; i++) {
    faceParentOffsets[i] = parentCount;
    if (srcForNewFace[i] >= 0 && srcForNewFace[i] < oldFaceCount) {
      parentCount++;
    }
  }
  faceParentOffsets[newFaceCount] = parentCount;

  const faceParents = new Uint32Array(parentCount);
  let cursor = 0;
  for (let i = 0; i < newFaceCount; i++) {
    const src = srcForNewFace[i];
    if (src >= 0 && src < oldFaceCount) {
      faceParents[cursor++] = src;
    }
  }

  return { faceRemap, faceParentOffsets, faceParents };
}

/**
 * Heuristic face-provenance derivation from a vertex provenance and the
 * old/new topologies.
 *
 * Each new face is matched to at most one old face by checking whether
 * every vertex of the new face descends (via `vertexProvenance.parents`)
 * from a vertex of the candidate old face.
 *
 * Algorithm:
 *  1. Build a vertex → list-of-incident-old-faces index from the old topology.
 *  2. For each new face, gather the set of OLD vertices reachable through
 *     `parents` for any of its corners.
 *  3. Score old faces by how many of their corners appear in that reachable
 *     set; the highest-scoring old face whose every corner is reachable
 *     wins. Ties break by lowest old-face index.
 *  4. Faces with no qualifying old face are recorded as "newly created"
 *     (empty parent bucket).
 *
 * This recovers correct mappings for every common topology op:
 *  - delete: kept faces match themselves; deleted faces map to FACE_REMOVED.
 *  - dissolve verts: ring of old faces collapses into one new face → first
 *    qualifying old face wins (single-parent simplification — multi-parent
 *    can be wired in later if needed).
 *  - subdivide: each new sub-face matches its parent face.
 *  - extrude / inset / bevel: cap and side faces have no qualifying parent
 *    (vertex parents include only the originating loop); they end up
 *    parentless and inherit defaults, which is the correct behaviour.
 *
 * Single-parent simplification keeps this implementation tight; the
 * downstream remappers all accept multi-parent CSRs, so a future tighter
 * provenance only needs to broaden the parent buckets.
 */
export function deriveFaceProvenance(
  oldTopology: PolygonTopology,
  newTopology: PolygonTopology,
  vertexProvenance: VertexProvenance,
): FaceProvenance {
  const oldFaceCount =
    oldTopology.offsets.length === 0 ? 0 : oldTopology.offsets.length - 1;
  const newFaceCount =
    newTopology.offsets.length === 0 ? 0 : newTopology.offsets.length - 1;

  // Fast path 1: pre-/post-counts match, vertex provenance is identity →
  // every face survived in place. Caller paths (transform-only ops, etc.)
  // hit this without needing to rebuild any structure.
  if (oldFaceCount === newFaceCount) {
    let allIdentity = true;
    for (let i = 0; i < vertexProvenance.remap.length; i++) {
      if (vertexProvenance.remap[i] !== i) {
        allIdentity = false;
        break;
      }
    }
    if (allIdentity) return identityFaceProvenance(newFaceCount);
  }

  // Build vertex → list-of-incident-old-faces index.
  const oldOffsets = oldTopology.offsets;
  const oldCorners = oldTopology.corners;
  // Determine the maximum vertex index across the OLD topology so the index
  // array is sized correctly (vertices that are no longer referenced still
  // need entries).
  let oldVertexCount = 0;
  for (let c = 0; c < oldCorners.length; c++) {
    if (oldCorners[c] + 1 > oldVertexCount) oldVertexCount = oldCorners[c] + 1;
  }
  // Also size for vertex provenance domain so out-of-range guards stay simple.
  if (vertexProvenance.remap.length > oldVertexCount) {
    oldVertexCount = vertexProvenance.remap.length;
  }
  const incidentCount = new Uint32Array(oldVertexCount);
  for (let c = 0; c < oldCorners.length; c++) incidentCount[oldCorners[c]]++;
  const incidentOffsets = new Uint32Array(oldVertexCount + 1);
  for (let i = 0; i < oldVertexCount; i++) {
    incidentOffsets[i + 1] = incidentOffsets[i] + incidentCount[i];
  }
  const incidentFaces = new Uint32Array(incidentOffsets[oldVertexCount]);
  const incidentCursor = new Uint32Array(oldVertexCount);
  for (let f = 0; f < oldFaceCount; f++) {
    const fs = oldOffsets[f];
    const fe = oldOffsets[f + 1];
    for (let c = fs; c < fe; c++) {
      const v = oldCorners[c];
      incidentFaces[incidentOffsets[v] + incidentCursor[v]] = f;
      incidentCursor[v]++;
    }
  }

  const newOffsets = newTopology.offsets;
  const newCorners = newTopology.corners;
  const { parentOffsets, parents } = vertexProvenance;

  const faceRemap = new Int32Array(oldFaceCount);
  faceRemap.fill(FACE_REMOVED);
  const newToOldParent = new Int32Array(newFaceCount);
  newToOldParent.fill(-1);

  // Reusable scratch buffer keyed by old face index — avoid allocations in the
  // inner loop by tracking which entries we've touched per new face.
  const score = new Int32Array(oldFaceCount);
  const touched: number[] = [];

  for (let nf = 0; nf < newFaceCount; nf++) {
    const ns = newOffsets[nf];
    const ne = newOffsets[nf + 1];
    const newFaceSize = ne - ns;
    if (newFaceSize === 0) continue;

    // Reset score buffer cells touched by the previous face.
    for (let i = 0; i < touched.length; i++) score[touched[i]] = 0;
    touched.length = 0;

    let bestFace = -1;
    let bestScore = -1;
    let allCornersHaveACandidate = true;

    for (let c = ns; c < ne; c++) {
      const newVert = newCorners[c];
      const ps =
        newVert < parentOffsets.length - 1 ? parentOffsets[newVert] : 0;
      const pe =
        newVert < parentOffsets.length - 1 ? parentOffsets[newVert + 1] : 0;
      if (ps === pe) {
        // Newly created vertex — no old-face constraint from this corner.
        continue;
      }
      // For this corner, mark every old face that is incident to any parent
      // vertex; whether a face wins is judged by total coverage across all
      // corners.
      let cornerHasIncidence = false;
      for (let p = ps; p < pe; p++) {
        const oldVert = parents[p];
        if (oldVert >= oldVertexCount) continue;
        const is = incidentOffsets[oldVert];
        const ie = incidentOffsets[oldVert + 1];
        for (let k = is; k < ie; k++) {
          const oldFace = incidentFaces[k];
          if (score[oldFace] === 0) touched.push(oldFace);
          score[oldFace]++;
          cornerHasIncidence = true;
        }
      }
      if (!cornerHasIncidence) allCornersHaveACandidate = false;
    }

    if (!allCornersHaveACandidate || touched.length === 0) {
      // No old face matches every corner → newly-created face.
      continue;
    }

    // Pick the old face whose old-corner-count is matched (i.e. the new face
    // covers all of its corners). Among ties, prefer the smallest face — a
    // plausible "primary parent" tiebreaker that's deterministic.
    for (let i = 0; i < touched.length; i++) {
      const oldFace = touched[i];
      const oldFaceSize = oldOffsets[oldFace + 1] - oldOffsets[oldFace];
      if (score[oldFace] >= oldFaceSize) {
        if (bestScore < 0 || oldFaceSize < bestScore) {
          bestScore = oldFaceSize;
          bestFace = oldFace;
        }
      }
    }

    // Fallback: if no old face was fully covered, accept the highest-scoring
    // candidate as the primary parent. This handles dissolve-faces (where
    // the new face is larger than any single old face) by picking a stable
    // representative parent.
    if (bestFace < 0) {
      let topScore = 0;
      for (let i = 0; i < touched.length; i++) {
        if (score[touched[i]] > topScore) {
          topScore = score[touched[i]];
          bestFace = touched[i];
        }
      }
    }

    if (bestFace >= 0) {
      newToOldParent[nf] = bestFace;
      // First-write-wins on faceRemap: if multiple new faces nominally share
      // an old face (face splits, e.g. subdivide), the lowest-indexed new
      // face becomes the canonical successor. Other splits still inherit
      // the parent's per-face data via faceParents.
      if (faceRemap[bestFace] === FACE_REMOVED) faceRemap[bestFace] = nf;
    }
  }

  // Build CSR parents from newToOldParent (single-parent per new face for
  // now). New faces with parent = -1 contribute zero entries.
  let parentTotal = 0;
  for (let nf = 0; nf < newFaceCount; nf++) {
    if (newToOldParent[nf] >= 0) parentTotal++;
  }
  const faceParentOffsets = new Uint32Array(newFaceCount + 1);
  const faceParents = new Uint32Array(parentTotal);
  let cursor = 0;
  for (let nf = 0; nf < newFaceCount; nf++) {
    faceParentOffsets[nf] = cursor;
    if (newToOldParent[nf] >= 0) {
      faceParents[cursor] = newToOldParent[nf];
      cursor++;
    }
  }
  faceParentOffsets[newFaceCount] = cursor;

  return { faceRemap, faceParentOffsets, faceParents };
}

/**
 * Project a per-face Uint16 attribute (e.g. material slot index) forward
 * through a face provenance. Each new face takes its first parent's value;
 * orphan new faces (no parents) get `default_`.
 *
 * Returns `undefined` when the result would be all zeroes — preserves the
 * "omit when empty" invariant on `FaceAttributeTable.materialIndex`.
 */
export function remapPerFaceUint16(
  oldArr: Uint16Array,
  prov: FaceProvenance,
  newFaceCount: number,
  default_ = 0,
): Uint16Array | undefined {
  if (newFaceCount === 0) return undefined;
  const out = new Uint16Array(newFaceCount);
  let anyNonZero = false;
  for (let i = 0; i < newFaceCount; i++) {
    const start = prov.faceParentOffsets[i];
    const end = prov.faceParentOffsets[i + 1];
    let v = default_;
    if (end > start) {
      const oldIdx = prov.faceParents[start];
      if (oldIdx < oldArr.length) v = oldArr[oldIdx];
    }
    out[i] = v;
    if (v !== 0) anyNonZero = true;
  }
  return anyNonZero ? out : undefined;
}

/**
 * Project a per-face Uint8 attribute (e.g. shading override) forward through
 * a face provenance. Same first-parent semantics as `remapPerFaceUint16`.
 */
export function remapPerFaceUint8(
  oldArr: Uint8Array,
  prov: FaceProvenance,
  newFaceCount: number,
  default_ = 0,
): Uint8Array | undefined {
  if (newFaceCount === 0) return undefined;
  const out = new Uint8Array(newFaceCount);
  let anyNonZero = false;
  for (let i = 0; i < newFaceCount; i++) {
    const start = prov.faceParentOffsets[i];
    const end = prov.faceParentOffsets[i + 1];
    let v = default_;
    if (end > start) {
      const oldIdx = prov.faceParents[start];
      if (oldIdx < oldArr.length) v = oldArr[oldIdx];
    }
    out[i] = v;
    if (v !== 0) anyNonZero = true;
  }
  return anyNonZero ? out : undefined;
}

/**
 * Project per-face-group membership forward through a face provenance. A new
 * face is in group X iff *any* of its parents was — the natural "union of
 * parents" semantic for an index-set attribute (matches `remapVertexGroups`).
 *
 * Empty groups post-remap are kept (matches Blender / vertex-group behaviour:
 * empty groups stay until the user explicitly deletes them).
 */
export function remapFaceGroups(
  groups: FaceGroup[],
  prov: FaceProvenance,
  newFaceCount: number,
): FaceGroup[] | undefined {
  if (groups.length === 0) return undefined;
  const next: FaceGroup[] = groups.map((g) => {
    const faces = new Set<number>();
    for (let i = 0; i < newFaceCount; i++) {
      const start = prov.faceParentOffsets[i];
      const end = prov.faceParentOffsets[i + 1];
      for (let j = start; j < end; j++) {
        if (g.faces.has(prov.faceParents[j])) {
          faces.add(i);
          break;
        }
      }
    }
    return { ...g, faces };
  });
  return next;
}

/**
 * Project an entire `FaceAttributeTable` forward through a face provenance.
 * Returns `undefined` when no field would carry meaningful data — preserves
 * the omit-when-empty invariant on `SceneObject.faceAttributes`.
 */
export function remapFaceAttributes(
  source: FaceAttributeTable | undefined,
  prov: FaceProvenance,
  newFaceCount: number,
): FaceAttributeTable | undefined {
  if (!source) return undefined;
  const next: FaceAttributeTable = {};
  if (source.materialIndex) {
    next.materialIndex = remapPerFaceUint16(
      source.materialIndex,
      prov,
      newFaceCount,
    );
  }
  if (source.shading) {
    next.shading = remapPerFaceUint8(source.shading, prov, newFaceCount);
  }
  if (source.groups) {
    next.groups = remapFaceGroups(source.groups, prov, newFaceCount);
  }
  return normalizeFaceAttributes(next);
}
